// src/components/modals/SelectPetForAdoptionModal.jsx
"use client";

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { petAPI } from '../../services/api';
import { adoptionAPI } from '../../services/adoptionAPI';
import { resolveImageUrl } from '../../utils/imageUtils';
import { getUserTypeColor } from '../../utils/userTypeUtils';
import { getPetName, getSpeciesName, getBreedName } from '../../utils/petDataUtils';
import { useAuth } from '../../hooks/useAuth';
import './SelectPetForAdoptionModal.css';
import QuickAddPetModal from './QuickAddPetModal';

const SelectPetForAdoptionModal = ({
  isOpen,
  onClose,
  onConfirm
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const { getUserType, user } = useAuth();

  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPets, setSelectedPets] = useState([]);
  const [error, setError] = useState(null);
  const [petsWithAdoptionPosts, setPetsWithAdoptionPosts] = useState(new Set());
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const scrollLockRef = useRef(0);

  // Get user type color
  const userType = getUserType();
  const userTypeColor = getUserTypeColor(userType);

  // Fetch user's pets and adoption posts
  useEffect(() => {
    if (isOpen) {
      fetchPets();
      fetchAdoptionPosts();
    } else {
      // Reset state when modal closes
      setSelectedPets([]);
      setError(null);
      setPetsWithAdoptionPosts(new Set());
    }
  }, [isOpen]);

  // Handle keyboard events and body scroll without losing page scroll position
  useEffect(() => {
    if (!isOpen) return;

    scrollLockRef.current = window.scrollY || window.pageYOffset || 0;

    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLockRef.current}px`;
    document.body.style.width = '100%';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.top = originalStyle.top;
      document.body.style.width = originalStyle.width;
      window.scrollTo(0, scrollLockRef.current || 0);
    };
  }, [isOpen]);

  const fetchPets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await petAPI.getMyPets();

      let petsData = [];
      if (response.data) {
        if (response.data.status === 'success' && response.data.data) {
          if (response.data.data.data && Array.isArray(response.data.data.data)) {
            petsData = response.data.data.data;
          } else if (Array.isArray(response.data.data)) {
            petsData = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          petsData = response.data;
        }
      }

      // Filter only pets that the current user owns (viewer_is_owner should be true)
      const ownedPets = petsData.filter(pet => pet.viewer_is_owner || String(pet.owner_id) === String(user?.id || user?.user_id));
      setPets(ownedPets);
    } catch (err) {
      console.error('Error fetching pets:', err);
      setError(t('adoption.modals.selectPet.error') || 'Failed to load pets');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdoptionPosts = async () => {
    try {
      const currentUserId = user?.id || user?.user_id;

      const response = await adoptionAPI.getAdoptionPosts({ filter: 'mine' });

      let postsData = [];
      if (response.data) {
        if (response.data.status === 'success' && response.data.data) {
          if (response.data.data.data && Array.isArray(response.data.data.data)) {
            postsData = response.data.data.data;
          } else if (Array.isArray(response.data.data)) {
            postsData = response.data.data;
          }
        } else if (Array.isArray(response.data)) {
          postsData = response.data;
        }
      }

      // Filter to only posts owned by current user (double-check)
      if (currentUserId) {
        postsData = postsData.filter(post => {
          const authorId = post.author?.id || post.user_id || post.user?.id || post.author_id;
          return String(authorId) === String(currentUserId);
        });
      }

      // Extract pet IDs from adoption posts
      // Only consider posts that are still "offered" (not "adopted")
      const petIdsWithPosts = new Set();
      postsData.forEach(post => {
        // Only consider active adoption posts (status should be 'offered', not 'adopted')
        const postStatus = post.status || post.adoption_status;
        if (postStatus === 'adopted') {
          return; // Skip adopted posts
        }

        // Helper to add ID ensuring it's a number for consistent comparison
        const addPetId = (id) => {
          if (id != null && id !== undefined && id !== '') {
            // Convert to number for consistent comparison
            const numId = Number(id);
            if (!isNaN(numId) && numId > 0) {
              petIdsWithPosts.add(numId);
              // Also add as string for compatibility
              petIdsWithPosts.add(String(id));
            }
          }
        };

        // Check pet_id (single)
        if (post.pet_id) {
          addPetId(post.pet_id);
        }

        // Check pet_ids (array or single)
        if (post.pet_ids) {
          if (Array.isArray(post.pet_ids)) {
            post.pet_ids.forEach(id => addPetId(id));
          } else {
            addPetId(post.pet_ids);
          }
        }

        // Check pets array if available
        if (post.pets && Array.isArray(post.pets)) {
          post.pets.forEach(pet => {
            if (pet.id) addPetId(pet.id);
          });
        }

        // Check pet_info - could be single object, array, or nested
        if (post.pet_info) {
          if (Array.isArray(post.pet_info)) {
            post.pet_info.forEach(pet => {
              if (pet && pet.id) addPetId(pet.id);
            });
          } else if (post.pet_info && post.pet_info.id) {
            addPetId(post.pet_info.id);
          }
        }
      });



      setPetsWithAdoptionPosts(petIdsWithPosts);
    } catch (err) {
      console.error('Error fetching adoption posts:', err);
      // Don't show error to user, just log it
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const togglePetSelection = (pet) => {
    // Don't allow selection if pet already has an adoption post
    if (petsWithAdoptionPosts.has(pet.id)) {
      return;
    }

    setSelectedPets(prev => {
      const isSelected = prev.some(p => p.id === pet.id);
      if (isSelected) {
        return prev.filter(p => p.id !== pet.id);
      } else {
        return [...prev, pet];
      }
    });
  };

  const hasAdoptionPost = (petId) => {
    if (!petId) return false;
    // Check both number and string versions for compatibility
    const numId = Number(petId);
    const strId = String(petId);
    return petsWithAdoptionPosts.has(numId) || petsWithAdoptionPosts.has(strId) || petsWithAdoptionPosts.has(petId);
  };

  const handleConfirm = () => {
    if (selectedPets.length === 0) {
      setError(t('adoption.modals.selectPet.noSelection') || 'Please select at least one pet');
      return;
    }
    onConfirm(selectedPets);
  };

  const handleQuickAddPetAdded = (newPet) => {
    setIsQuickAddOpen(false);
    
    // Convert ID to number explicitly for consistency
    if (newPet && newPet.id) {
      newPet.id = Number(newPet.id);
    }

    setPets(prev => [...prev, newPet]);
    
    // Auto-select the newly added pet
    setSelectedPets(prev => {
      const isAlreadySelected = prev.some(p => p.id === newPet.id);
      if (!isAlreadySelected) {
        return [...prev, newPet];
      }
      return prev;
    });
  };

  const getPetImage = (pet) => {
    if (!pet) return "/images/no-image-pet.png";

    // Handle profile_image_path which might be a string or an object { url, path, ... }
    const profilePath =
      typeof pet.profile_image_path === "string"
        ? pet.profile_image_path
        : pet.profile_image_path?.url || pet.profile_image_path?.path;

    // Try to resolve image from multiple possible fields
    const imageUrl = resolveImageUrl(
      // Transformed field from usePets (primaryImageUrl)
      pet.primaryImageUrl,
      // Raw backend-style fields
      pet.primary_image_url,
      profilePath,
      pet.image,
      pet.avatar,
      pet.profile_image_url,
      pet.profileImageUrl,
      pet.photo,
      pet.pictures?.[0]?.url,
      pet.media?.[0]?.url,
      pet.gallery_images?.[0]?.url,
      pet.gallery_images?.[0]?.path
    );

    return imageUrl || "/images/no-image-pet.png";
  };

  // Helper function to get display value with fallback
  const getDisplayValue = (value, fallback) => {
    return value || (t(fallback) || 'Unknown');
  };

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className="select-pet-modal-overlay"
      onClick={handleBackdropClick}
    >
      <div
        className="select-pet-modal"
        onClick={(e) => e.stopPropagation()}
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        {/* Header */}
        <div className="select-pet-modal-header">
          <h2 className="select-pet-modal-title">
            {t('adoption.modals.selectPet.title') || 'Select Pet(s) for Adoption'}
          </h2>
          <button
            className="select-pet-modal-close"
            onClick={onClose}
            disabled={loading}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="select-pet-modal-content">
          {loading ? (
            <div className="select-pet-modal-loading">
              <div className="select-pet-modal-spinner"></div>
              <p>{t('adoption.modals.selectPet.loading') || 'Loading pets...'}</p>
            </div>
          ) : error ? (
            <div className="select-pet-modal-error">
              <p>{error}</p>
              <button onClick={fetchPets}>
                {t('adoption.modals.selectPet.retry') || 'Retry'}
              </button>
            </div>
          ) : (
            <>
              <p className="select-pet-modal-instruction">
                {pets.length === 0 
                  ? (t('adoption.modals.selectPet.noPets') || 'You don\'t have any pets yet.')
                  : (t('adoption.modals.selectPet.instruction') || 'Select one or more pets you want to offer for adoption')}
              </p>

              {/* Selected Pets Info */}
              {selectedPets.length > 0 && (
                <div className="select-pet-selected-info">
                  <h3>{t('adoption.modals.selectPet.selectedPets') || 'Selected Pet(s):'}</h3>
                  <div className="select-pet-selected-list">
                    {selectedPets.map(pet => (
                      <div key={pet.id} className="select-pet-selected-item">
                        <img
                          src={getPetImage(pet)}
                          alt={getPetName(pet)}
                          onError={(e) => {
                            e.target.src = "/images/no-image-pet.png";
                          }}
                        />
                        <div className="select-pet-selected-details">
                          <p className="select-pet-selected-name">
                            {getDisplayValue(getPetName(pet), 'adoption.modals.selectPet.unknownPet')}
                          </p>
                          <p className="select-pet-selected-info-text">
                            {getDisplayValue(getSpeciesName(pet.species), 'adoption.modals.selectPet.unknownSpecies')} • {getDisplayValue(getBreedName(pet.breed), 'adoption.modals.selectPet.unknownBreed')}
                            {pet.gender && ` • ${pet.gender}`}
                            {pet.color && ` • ${pet.color}`}
                            {(pet.calculated_age || pet.age_formatted || pet.age) 
                              ? ` • ${pet.calculated_age || pet.age_formatted || pet.age}` 
                              : (pet.birth_date && ` • ${new Date(pet.birth_date).toLocaleDateString()}`)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pets Grid */}
              <div className="select-pet-grid">
                {pets.map(pet => {
                  const isSelected = selectedPets.some(p => p.id === pet.id);
                  const hasAdoption = hasAdoptionPost(pet.id);
                  return (
                    <div
                      key={pet.id}
                      className={`select-pet-card ${isSelected ? 'selected' : ''} ${hasAdoption ? 'disabled' : ''}`}
                      onClick={() => togglePetSelection(pet)}
                      style={{
                        cursor: hasAdoption ? 'not-allowed' : 'pointer',
                        opacity: hasAdoption ? 0.6 : 1,
                        position: 'relative'
                      }}
                    >
                      {hasAdoption && (
                        <div className="select-pet-card-disabled-overlay">
                          <span className="select-pet-card-disabled-badge">
                            {t('adoption.modals.selectPet.hasAdoptionPost') || 'Already has adoption offer'}
                          </span>
                        </div>
                      )}
                      <div className="select-pet-card-checkbox">
                        {isSelected && <span>✓</span>}
                      </div>
                      <img
                        src={getPetImage(pet)}
                        alt={getPetName(pet)}
                        className="select-pet-card-image"
                        onError={(e) => {
                          e.target.src = "/images/no-image-pet.png";
                        }}
                        style={{
                          filter: hasAdoption ? 'grayscale(50%)' : 'none'
                        }}
                      />
                      <div className="select-pet-card-info">
                        <h4 className="select-pet-card-name">
                          {getDisplayValue(getPetName(pet), 'adoption.modals.selectPet.unknownPet')}
                        </h4>
                        <p className="select-pet-card-details">
                          {getDisplayValue(getSpeciesName(pet.species), 'adoption.modals.selectPet.unknownSpecies')} • {getDisplayValue(getBreedName(pet.breed), 'adoption.modals.selectPet.unknownBreed')}
                        </p>
                        {pet.gender && (
                          <p className="select-pet-card-gender">{pet.gender}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                
                {/* Add New Pet Card */}
                <div
                  className="select-pet-card add-pet-card"
                  onClick={() => setIsQuickAddOpen(true)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed #e0e0e0',
                    backgroundColor: '#fafafa',
                    textAlign: 'center',
                    padding: '20px',
                    minHeight: '220px',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = userTypeColor || '#8548A8';
                    e.currentTarget.style.backgroundColor = '#f0ebf5';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e0e0e0';
                    e.currentTarget.style.backgroundColor = '#fafafa';
                  }}
                >
                  <div style={{ 
                    fontSize: '3rem', 
                    color: userTypeColor || '#8548A8', 
                    marginBottom: '10px',
                    lineHeight: '1',
                    fontWeight: '300'
                  }}>+</div>
                  <p style={{ 
                    fontWeight: '600', 
                    color: '#555', 
                    margin: 0,
                    fontSize: '1rem',
                  }}>
                    {t('adoption.modals.selectPet.petNotAddedYet') || 'Pet not added yet?'}
                  </p>
                  <p style={{
                    color: userTypeColor || '#8548A8',
                    fontWeight: '500',
                    marginTop: '4px',
                    fontSize: '0.9rem'
                  }}>
                    {t('adoption.modals.selectPet.addPet') || 'Add Pet'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!loading && pets.length > 0 && (
          <div className="select-pet-modal-footer">
            <button
              className="select-pet-modal-cancel"
              onClick={onClose}
            >
              {t('common.cancel') || 'Cancel'}
            </button>
            <button
              className="select-pet-modal-confirm"
              onClick={handleConfirm}
              disabled={selectedPets.length === 0}
              style={{ backgroundColor: userTypeColor, color: '#FFFFFF' }}
            >
              {t('adoption.modals.selectPet.confirm') || 'Confirm Selection'}
            </button>
          </div>
        )}
      </div>

      <QuickAddPetModal
        isOpen={isQuickAddOpen}
        onClose={() => setIsQuickAddOpen(false)}
        onPetAdded={handleQuickAddPetAdded}
        userTypeColor={userTypeColor}
      />
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SelectPetForAdoptionModal;

